/**
 * SEO compartido entre la SPA (Vite) y scripts/prerender.mjs (Node, que ejecuta
 * este archivo directamente con type-stripping). Por eso:
 *   · un solo archivo, sin imports (ni alias "@/" ni relativos);
 *   · solo sintaxis de tipos que Node pueda borrar: nada de enums, namespaces
 *     ni parameter properties.
 * Los textos por defecto están en castellano, que es lo que se prerenderiza; la
 * SPA puede pasar etiquetas traducidas donde las hay.
 */

export const SITE_ORIGIN = "https://recine.es";
export const BRAND = "RE:CINE";
export const DEFAULT_TITLE = "RE:CINE — El cine que merece ser descubierto";
/** Tarjetas para compartir (1200×630, 31-10-2026): portada, una por ciudad y
 *  una por cine de la tarifa, en el color del cine. Hechas una vez; las fichas
 *  de película usan su fotograma. */
const OG_BASE = "https://asrgzcggnwnsvpcsmxxw.supabase.co/storage/v1/object/public/media/brand/og";
export const DEFAULT_OG_IMAGE = `${OG_BASE}/home.png`;
export const cityOgImage = (citySlug: string) => `${OG_BASE}/city-${citySlug}.png`;
const CINEMA_OG = new Set([
  "cinemes-texas",
  "zumzeig-cinema",
  "cinema-edison",
  "cinemes-girona",
  "cines-abc-el-saler",
  "cines-abc-gran-turia",
  "cines-abc-park",
  "cines-babel",
  "cines-lys",
]);
/** La tarjeta del cine, o null si es uno nuevo sin tarjeta todavía. */
export function cinemaOgImage(name: string): string | null {
  const slug = cinemaSlug(name);
  return CINEMA_OG.has(slug) ? `${OG_BASE}/cinema-${slug}.png` : null;
}
export const TIME_ZONE = "Europe/Madrid";

/** Fotos de media/hero que no son de butacas (1-11-2026: las cabeceras de
 *  RE:CINE, siempre butacas). Se quedan en el almacenamiento, fuera de la web.
 *  Lo usan la SPA (src/lib/heroImages.ts) y el prerender, que precarga la
 *  misma foto del día. */
const HERO_EXCLUDE = new Set(["artistic-frames-dw-x5vwja2y-unsplash-1775145620943.jpg"]);
export const isHeroPhoto = (name: string) => !HERO_EXCLUDE.has(name);

/** Precio mensual de la suscripción, publicado el 28-10-2026. Única fuente
 *  para /suscripcion, los CTA (interpolado en los textos), el prerender y
 *  llms.txt. */
export const PRICE_EUR = 18;

/* ------------------------------------------------------------------ */
/*  Ciudades                                                           */
/* ------------------------------------------------------------------ */

export type CityDef = { slug: string; name: string; aliases: readonly string[] };

/** Ciudades con URL propia. `name` coincide con cinemas.city tras normalizar;
 *  los alias se pliegan (el cine de Gandía se lista bajo Valencia). */
export const CITIES: readonly CityDef[] = [
  { slug: "barcelona", name: "Barcelona", aliases: [] },
  { slug: "valencia", name: "Valencia", aliases: ["Gandía", "Gandia", "València"] },
];

export function cityForName(name: string | null | undefined): CityDef | null {
  const n = (name ?? "").trim();
  if (!n) return null;
  return CITIES.find((c) => c.name === n || c.aliases.includes(n)) ?? null;
}

export function cityForSlug(slug: string | null | undefined): CityDef | null {
  return CITIES.find((c) => c.slug === slug) ?? null;
}

/* ------------------------------------------------------------------ */
/*  Slugs y rutas                                                      */
/* ------------------------------------------------------------------ */

/** Igual que public.slugify en la BD (migración 20260930100000_movies_slug). */
export function slugify(text: string, maxLen = 80): string {
  const v = String(text ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (v.length <= maxLen) return v;
  return v.slice(0, maxLen).replace(/-[^-]*$/, "").replace(/^-+|-+$/g, "");
}

/** Slug de un cine (sin columna en la BD: se calcula del nombre). */
export function cinemaSlug(name: string): string {
  return slugify(name, 200);
}

export const moviePath = (citySlug: string, movieSlug: string) => `/${citySlug}/pelicula/${movieSlug}`;
export const cinemaPath = (name: string) => `/cines/${cinemaSlug(name)}`;
export const cityPath = (citySlug: string) => `/${citySlug}`;
/** URL absoluta sin barra final (salvo la portada, que es "https://recine.es/"). */
export const absUrl = (path: string) => (path === "/" || !path ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${path.replace(/\/+$/, "")}`);

/** Enlace a la ficha de una película en una ciudad; sin slug o sin ciudad
 *  conocida cae a la URL antigua, que redirige (301) a la nueva. */
export function movieHref(
  movie: { id: string; slug?: string | null },
  cityName: string | null | undefined,
): string {
  const city = cityForName(cityName);
  return movie.slug && city ? moviePath(city.slug, movie.slug) : `/peliculas/${movie.id}`;
}

/* ------------------------------------------------------------------ */
/*  Fechas en hora de Madrid                                           */
/* ------------------------------------------------------------------ */

/** YYYY-MM-DD de hoy en Madrid. */
export function madridToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** Minutos desde medianoche, ahora, en Madrid. */
export function madridMinutesNow(now: Date = new Date()): number {
  const [h, m] = new Intl.DateTimeFormat("en-GB", {
    timeZone: TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  })
    .format(now)
    .split(":")
    .map(Number);
  return h * 60 + m;
}

/** Desfase de Madrid ("+02:00") en esa fecha y hora locales. */
function madridOffset(date: string, time: string): string {
  const guess = new Date(`${date}T${time.slice(0, 5)}:00Z`);
  const tz =
    new Intl.DateTimeFormat("en-US", { timeZone: TIME_ZONE, timeZoneName: "longOffset" })
      .formatToParts(guess)
      .find((p) => p.type === "timeZoneName")?.value ?? "";
  const m = /GMT([+-]\d{2}):?(\d{2})?/.exec(tz);
  return m ? `${m[1]}:${m[2] ?? "00"}` : "+01:00";
}

/** Fecha y hora locales de Madrid en ISO 8601 con desfase: 2026-10-02T20:15:00+02:00. */
export function madridIso(date: string, time: string): string {
  return `${date}T${time.slice(0, 5)}:00${madridOffset(date, time)}`;
}

/** "lunes, 5 de octubre" en castellano. */
export function formatDayEs(date: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString("es-ES", {
    timeZone: "UTC",
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

/* ------------------------------------------------------------------ */
/*  Títulos y descripciones                                            */
/* ------------------------------------------------------------------ */

export const TITLE_MAX = 65;
const BRAND_SUFFIX = ` | ${BRAND}`;
const SEP = " · ";

const clean = (s: string | null | undefined) => (s ?? "").replace(/\s+/g, " ").trim();

/** Une los segmentos con " · " y añade " | RE:CINE" si cabe en 65 caracteres.
 *  Si no cabe ni sin marca, quita segmentos del final; en último caso corta. */
export function fitTitle(segments: (string | null | undefined)[]): string {
  const segs = segments.map(clean).filter(Boolean);
  while (segs.length) {
    const t = segs.join(SEP);
    if (t.length + BRAND_SUFFIX.length <= TITLE_MAX) return t + BRAND_SUFFIX;
    if (t.length <= TITLE_MAX) return t;
    if (segs.length === 1) break;
    segs.pop();
  }
  const only = segs[0] ?? BRAND;
  const cut = only.slice(0, TITLE_MAX - 1).replace(/\s+\S*$/, "");
  return `${cut || only.slice(0, TITLE_MAX - 1)}…`;
}

/** La primera combinación que cabe entera (sin quitar segmentos) gana. */
export function fitTitleFirst(options: (string | null | undefined)[][]): string {
  for (const segs of options) {
    const joined = segs.map(clean).filter(Boolean).join(SEP);
    if (joined && joined.length <= TITLE_MAX) return fitTitle(segs);
  }
  return fitTitle(options[options.length - 1] ?? []);
}

/** Descripción de hasta 155 caracteres, cortada en frontera de palabra. */
export function cleanDescription(text: string | null | undefined, max = 155): string {
  const t = clean(text);
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1).replace(/\s+\S*$/, "").replace(/[,.;:]$/, "")}…`;
}

/** Páginas legales: ruta → título (el mismo que su <h1> en la SPA). */
export const LEGAL_PAGES: readonly { path: string; title: string }[] = [
  { path: "/privacidad", title: "Política de Privacidad" },
  { path: "/cookies", title: "Política de Cookies" },
  { path: "/aviso-legal", title: "Aviso Legal" },
  { path: "/terminos", title: "Términos y Condiciones" },
];
export const legalDescription = (title: string) =>
  `${title} de ${BRAND}, la tarifa plana de cine independiente en Barcelona y Valencia.`;

/** Título de la portada: la marca delante ("RE:CINE · Cine independiente en …"). */
export function homeTitle(tagline: string): string {
  const t = `${BRAND}${SEP}${clean(tagline)}`;
  return t.length <= TITLE_MAX ? t : fitTitle([tagline]);
}

/** Títulos de ficha (29-10-2026): con la forma en que se busca
 *  («dónde ver X en Valencia», «X cines Valencia»). Antes eran
 *  «X · Valencia · horarios…»: posición media 8 y 2 % de clics en Search
 *  Console (28 días hasta el 26-10). De más largo a más corto: gana el
 *  primero que cabe. */
export type MovieCityLabels = { titleLong: string; titleShort: string; titleMin: string; description: string };
export const MOVIE_CITY_LABELS_ES: MovieCityLabels = {
  titleLong: "{{title}} en {{city}}: horarios y cines",
  titleShort: "{{title}} en {{city}}: horarios",
  titleMin: "{{title}} en {{city}}",
  description: "Horarios de {{title}} en {{city}}: {{cinemas}}.",
};

const fill = (tpl: string, vars: Record<string, string>) =>
  tpl.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? "");

/** Título tal como se enseña (28-10-2026). El scraper trae a veces el título
 *  EN MAYÚSCULAS y con el festival entre paréntesis: «SUPERTHINGS: LA PELÍCULA
 *  (41º MOSTRA DE VALÈNCIA)» salía así en <title>, <h1> y schema. Se quita el
 *  paréntesis final si habla de un festival, ciclo o muestra, y si todo está
 *  en mayúsculas (4 letras o más) pasa a frase, con mayúscula tras «: », «¿»…
 *  No toca ni el slug ni la base: es solo presentación. */
const FESTIVAL_PAREN =
  /\s*\((?:\d+\s*[ºª°]?\s*)?(?:mostra|festival|muestra|ciclo|semana|setmana|retrospectiva|cineclub|cine club|zinemaldia|seminci|sitges|edici[oó]n)[^)]*\)\s*$/i;
export function displayTitle(raw: string | null | undefined): string {
  const original = clean(raw);
  let t = original.replace(FESTIVAL_PAREN, "").trim() || original;
  const letters = t.replace(/[^\p{L}]/gu, "");
  if (letters.length >= 4 && letters === letters.toLocaleUpperCase("es") && letters !== letters.toLocaleLowerCase("es")) {
    t = t
      .toLocaleLowerCase("es")
      .replace(/(^|[:.!?¡¿]\s*|\s-\s|\s–\s)(\p{L})/gu, (_m, pre: string, ch: string) => pre + ch.toLocaleUpperCase("es"));
  }
  return t;
}

/** "La Bola Negra · Valencia · horarios en cines independientes". */
export function movieCityMeta(opts: {
  title: string;
  cityName: string;
  cinemaNames: string[];
  synopsis?: string | null;
  labels?: MovieCityLabels;
}): { title: string; description: string } {
  const l = opts.labels ?? MOVIE_CITY_LABELS_ES;
  const shown = displayTitle(opts.title);
  const v = { title: shown, city: opts.cityName };
  const title = fitTitleFirst([[fill(l.titleLong, v)], [fill(l.titleShort, v)], [fill(l.titleMin, v)], [shown]]);
  const lead = fill(l.description, {
    title: shown,
    city: opts.cityName,
    cinemas: opts.cinemaNames.join(", "),
  });
  return { title, description: cleanDescription(`${lead} ${clean(opts.synopsis)}`) };
}

/** Títulos y descripciones de cine y ciudad (31-10-2026): pensados para
 *  «cartelera + cine» («cartelera cines babel», «cine texas cartelera»), que
 *  ya llegaban a las fichas de cine en posición 10–19. Sin números («hoy 12
 *  películas»): Google no vuelve cada día y el número quedaría viejo. Los
 *  textos salen de seo.cinemaDetail.* y seo.city.* (prerender en castellano,
 *  SPA en el idioma del visitante). */
export type CinemaLabels = { titleLong: string; titleShort: string; titleMin: string; description: string; showing: string };

/** Con la ciudad entre paréntesis salvo si ya va en el nombre. */
const cinemaVars = (name: string, cityName: string | null | undefined) => {
  const n = clean(name);
  const city = clean(cityName);
  return { name: n, city: city && !slugify(n).includes(slugify(city)) ? city : "" };
};
const dropEmptyCity = (t: string) => t.replace(/\s*\(\s*\)/g, "").replace(/\s+([:,.])/g, "$1").replace(/\s+/g, " ").trim();

/** "Cartelera de Cines Babel (Valencia): horarios". De más largo a más corto. */
export function cinemaMeta(opts: { name: string; cityName: string | null; labels: CinemaLabels }): string {
  const v = cinemaVars(opts.name, opts.cityName);
  const l = opts.labels;
  return fitTitleFirst([
    [dropEmptyCity(fill(l.titleLong, v))],
    [dropEmptyCity(fill(l.titleShort, v))],
    [dropEmptyCity(fill(l.titleMin, v))],
    [v.name],
  ]);
}

/** Las películas con más pases en los próximos 7 días (empate: por título). */
export function topTitles(rows: { title: string; date: string }[], today: string, n = 3): string[] {
  const end = new Date(Date.parse(`${today}T12:00:00Z`) + 6 * 864e5).toISOString().slice(0, 10);
  const count = new Map<string, number>();
  for (const r of rows) {
    if (r.date < today || r.date > end) continue;
    const t = displayTitle(r.title);
    count.set(t, (count.get(t) ?? 0) + 1);
  }
  return [...count.entries()]
    .sort(([a, x], [b, y]) => y - x || a.localeCompare(b, "es"))
    .slice(0, n)
    .map(([t]) => t);
}

const listJoin = (items: string[]) => items.join(", ");

/** "Cartelera de Cines Babel en Valencia: horarios de hoy y de toda la semana.
 *  En cartel: La bola negra, Naza, Hamnet. {descripción de la sala}" */
export function cinemaDescription(opts: {
  name: string;
  cityName: string | null;
  titles: string[];
  about?: string | null;
  labels: CinemaLabels;
}): string {
  const v = cinemaVars(opts.name, opts.cityName);
  const lead = dropEmptyCity(fill(opts.labels.description, { ...v, city: clean(opts.cityName) }));
  const showing = opts.titles.length ? fill(opts.labels.showing, { titles: listJoin(opts.titles) }) : "";
  return cleanDescription([lead, showing, clean(opts.about)].filter(Boolean).join(" "));
}

/** "Cartelera de cine independiente en Valencia: horarios de hoy y de toda la
 *  semana. En cartel: … En Cines Babel, Cines Lys…" */
export function cityDescription(opts: {
  cityName: string;
  cinemaNames: string[];
  titles: string[];
  labels: { description: string; showing: string; cinemas: string };
}): string {
  const lead = fill(opts.labels.description, { city: opts.cityName });
  const showing = opts.titles.length ? fill(opts.labels.showing, { titles: listJoin(opts.titles) }) : "";
  // Los cines al final: si no caben, se corta la lista y no lo principal.
  const where = opts.cinemaNames.length ? fill(opts.labels.cinemas, { cinemas: listJoin(opts.cinemaNames) }) : "";
  return cleanDescription([lead, showing, where].filter(Boolean).join(" "));
}

/* ------------------------------------------------------------------ */
/*  Datos estructurados (schema.org)                                   */
/* ------------------------------------------------------------------ */

type Json = Record<string, unknown>;

export type LdCinema = { name: string; address?: string | null; cityName?: string | null; image?: string | null };

function postalAddress(address: string | null | undefined, cityName: string | null | undefined): Json | undefined {
  const street = clean(address);
  if (!street) return undefined;
  const postal = /\b(\d{5})\b/.exec(street)?.[1];
  return {
    "@type": "PostalAddress",
    streetAddress: street,
    ...(postal ? { postalCode: postal } : {}),
    ...(cityName ? { addressLocality: cityName } : {}),
    addressCountry: "ES",
  };
}

function theaterNode(c: LdCinema): Json {
  const address = postalAddress(c.address, c.cityName);
  return {
    "@type": "MovieTheater",
    name: c.name,
    url: absUrl(cinemaPath(c.name)),
    ...(address ? { address } : {}),
  };
}

/* ------------------------------------------------------------------ */
/*  Imágenes para compartir (29-10-2026)                               */
/* ------------------------------------------------------------------ */

const OWN_STORAGE = "https://asrgzcggnwnsvpcsmxxw.supabase.co/storage/v1/object/public/";

/** La imagen solo si está en nuestro Storage. Las externas (miniaturas de
 *  Google, webs de las salas) pueden caer o cambiar sin aviso: mejor la de
 *  marca que una rota o de baja calidad al compartir. */
export function ownImage(url: string | null | undefined): string | null {
  return url && url.startsWith(OWN_STORAGE) ? url : null;
}

/** Recorte horizontal al vuelo (centro) con el redimensionador de Supabase,
 *  para fichas sin la imagen del editor de punto focal. 600×315 y no
 *  1200×630: el redimensionador no amplía, y con portadas de 600–1000 px de
 *  ancho pedir 1200 devolvía 600×630 (casi cuadrada). 600×315 es el mínimo
 *  de Facebook para la vista grande y sale en 1,91:1 con casi todas.
 *  `format=origin`: JPEG/PNG como el original (no todos los lectores de
 *  enlaces aceptan WebP). */
export function ogCropUrl(url: string | null | undefined): string | null {
  const own = ownImage(url);
  if (!own) return null;
  return `${own.replace("/storage/v1/object/public/", "/storage/v1/render/image/public/")}?width=600&height=315&resize=cover&quality=85&format=origin`;
}

/* ------------------------------------------------------------------ */
/*  Imágenes con punto de interés (29-10-2026)                         */
/* ------------------------------------------------------------------ */

export type ImageFocal = {
  image_focal_x?: number | null;
  image_focal_y?: number | null;
  image_width?: number | null;
  image_height?: number | null;
  movie_images?: { image_url: string; has_sizes?: boolean | null }[] | null;
};

/** Tamaños propios de la portada (5-11-2026): `<ruta>.320|640|1280.webp`, la
 *  foto entera, sin ampliar y con caché de un año. Los genera
 *  `SCRAPER/cover_sizes.py`, que marca `movie_images.has_sizes`. Sin marca se
 *  sigue con el Transform API, que tarda hasta 2 s en frío, da 429 con muchas
 *  fotos a la vez y se corta al pasar el cupo si hay tope de gasto. */
const OWN_SIZES = [320, 640, 1280];
export function hasOwnSizes(url: string, focal?: ImageFocal | null): boolean {
  return !!focal?.movie_images?.some((i) => i.image_url === url && i.has_sizes);
}
function ownSizeUrl(url: string, need: number): string {
  // Un 15 % corto no se nota y evita bajar el doble de bytes.
  return `${url}.${OWN_SIZES.find((s) => need <= s * 1.15) ?? 1280}.webp`;
}

/** ¿Tiene punto de interés distinto del centro? */
export function hasFocal(f: ImageFocal | null | undefined): boolean {
  const x = f?.image_focal_x;
  const y = f?.image_focal_y;
  return x != null && y != null && !(x === 50 && y === 50);
}

/**
 * URL del redimensionador de Supabase para pintar una foto en una caja de
 * `width`×`height` px con object-fit: cover.
 *
 * Sin punto de interés, recorta el servidor (resize=cover, por el centro),
 * que es lo más ligero. CON punto de interés no puede recortar el servidor:
 * devolvería ya el centro y object-position no tendría nada que mover (*Todo
 * es cárcel*, 29-10-2026: punto en 3 % 67 %, póster 2:3 sacado del centro de
 * una foto 16:9). Entonces se pide la foto ENTERA (sin alto, resize=contain)
 * con el ancho justo para cubrir la caja, y el navegador recorta por el
 * punto. Sin las medidas de la foto se supone apaisada (16:9), el peor caso.
 * Misma regla que RE:PÚBLICO (`coverScale`). La usan la SPA y el prerender
 * (las precargas tienen que pedir la misma URL que la página).
 */
export function renderImageUrl(url: string, width: number, height: number, focal?: ImageFocal | null, quality = 70): string {
  const MARKER = "/storage/v1/object/public/";
  if (!url.includes(MARKER)) return url;
  const base = url.replace(MARKER, "/storage/v1/render/image/public/");
  const sep = base.includes("?") ? "&" : "?";
  if (!hasFocal(focal) && !hasOwnSizes(url, focal)) return `${base}${sep}width=${width}&height=${height}&resize=cover&quality=${quality}`;
  return renderFullImageUrl(url, width, height, focal, quality);
}

/** La foto entera, con ancho de sobra para cubrir una caja de `width`×`height`
 *  (el recorte lo hace el navegador con object-position). Para cajas que
 *  cambian de forma según la pantalla (31-10-2026: la fila de Horarios es
 *  16:10 en escritorio y 3:4 en móvil; con el recorte del servidor a 16:10,
 *  el móvil recortaba un recorte y de «Hope» solo quedaba la cara). */
export function renderFullImageUrl(url: string, width: number, height: number, dims?: ImageFocal | null, quality = 70): string {
  const MARKER = "/storage/v1/object/public/";
  if (!url.includes(MARKER)) return url;
  const base = url.replace(MARKER, "/storage/v1/render/image/public/");
  const sep = base.includes("?") ? "&" : "?";
  const aspect =
    dims?.image_width && dims?.image_height ? dims.image_width / dims.image_height : Math.max(16 / 9, width / height);
  const w = Math.ceil(Math.max(width, height * aspect));
  if (hasOwnSizes(url, dims)) return ownSizeUrl(url, w);
  return `${base}${sep}width=${w}&resize=contain&quality=${quality}`;
}

/** /cines: los cines como lista de MovieTheater con su dirección. */
export function cinemaListLd(cinemas: LdCinema[]): Json {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: cinemas.map((c, i) => ({ "@type": "ListItem", position: i + 1, item: theaterNode(c) })),
  };
}

export function movieTheaterLd(c: LdCinema & { description?: string | null }): Json {
  return {
    "@context": "https://schema.org",
    ...theaterNode(c),
    ...(c.image ? { image: c.image } : {}),
    ...(c.description ? { description: cleanDescription(c.description, 300) } : {}),
  };
}

export function breadcrumbLd(items: { name: string; path: string }[]): Json {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: absUrl(it.path),
    })),
  };
}

export function itemListLd(paths: string[]): Json {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: paths.map((p, i) => ({ "@type": "ListItem", position: i + 1, url: absUrl(p) })),
  };
}

export type LdScreening = {
  date: string;
  time: string;
  cinema: LdCinema;
  ticketUrl?: string | null;
};

/** Grafo Movie + ScreeningEvent (hasta 30 pases) de una película en una ciudad. */
export function movieCityLd(opts: {
  path: string;
  title: string;
  synopsis?: string | null;
  durationMinutes?: number | null;
  image?: string | null;
  /** Año de producción (movies.year), cuando se sabe. */
  year?: number | null;
  screenings: LdScreening[];
}): Json {
  const url = absUrl(opts.path);
  const movieId = `${url}#movie`;
  const name = displayTitle(opts.title);
  const movie: Json = {
    "@type": "Movie",
    "@id": movieId,
    name,
    url,
    ...(opts.year ? { dateCreated: String(opts.year) } : {}),
    ...(opts.image ? { image: opts.image } : {}),
    ...(opts.synopsis ? { description: cleanDescription(opts.synopsis, 300) } : {}),
    ...(opts.durationMinutes ? { duration: `PT${opts.durationMinutes}M` } : {}),
  };
  const events = opts.screenings.slice(0, 30).map((s) => {
    const start = madridIso(s.date, s.time);
    const end =
      opts.durationMinutes && opts.durationMinutes > 0
        ? new Date(Date.parse(start) + opts.durationMinutes * 60_000)
        : null;
    return {
      "@type": "ScreeningEvent",
      name,
      startDate: start,
      ...(end ? { endDate: madridIso(madridToday(end), minutesToHm(end)) } : {}),
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      location: theaterNode(s.cinema),
      workPresented: { "@id": movieId },
      ...(opts.image ? { image: opts.image } : {}),
      ...(s.ticketUrl
        ? { offers: { "@type": "Offer", url: s.ticketUrl, availability: "https://schema.org/InStock" } }
        : {}),
    };
  });
  return { "@context": "https://schema.org", "@graph": [movie, ...events] };
}

/** Pases de una sala (31-10-2026): un ScreeningEvent por pase, con la
 *  película enlazada a su ficha. Para la página del cine, que es la que sale
 *  en «cartelera + cine». Hasta 30, los más cercanos. */
export function cinemaScreeningsLd(opts: {
  cinema: LdCinema;
  items: { date: string; time: string; title: string; path: string; durationMinutes?: number | null; image?: string | null }[];
}): Json {
  const events = opts.items.slice(0, 30).map((s) => {
    const name = displayTitle(s.title);
    const start = madridIso(s.date, s.time);
    const end = s.durationMinutes && s.durationMinutes > 0 ? new Date(Date.parse(start) + s.durationMinutes * 60_000) : null;
    return {
      "@type": "ScreeningEvent",
      name,
      startDate: start,
      ...(end ? { endDate: madridIso(madridToday(end), minutesToHm(end)) } : {}),
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      location: theaterNode(opts.cinema),
      workPresented: { "@type": "Movie", name, url: absUrl(s.path), ...(s.image ? { image: s.image } : {}) },
      ...(s.image ? { image: s.image } : {}),
    };
  });
  return { "@context": "https://schema.org", "@graph": events };
}

function minutesToHm(d: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(d);
}

/** Une varios nodos (con o sin @context propio) en un solo @graph. */
export function ldGraph(...nodes: Json[]): Json {
  return {
    "@context": "https://schema.org",
    "@graph": nodes.flatMap((n) => {
      if (Array.isArray(n["@graph"])) return n["@graph"] as Json[];
      const copy = { ...n };
      delete copy["@context"];
      return [copy];
    }),
  };
}

/** <script> JSON-LD seguro dentro de HTML (escapa "<"). */
export function jsonLdScript(data: Json): string {
  return `<script type="application/ld+json" data-prerender>${JSON.stringify(data).replace(/</g, "\\u003c")}</script>`;
}

/* ------------------------------------------------------------------ */
/*  Portada: Organization + WebSite (28-10-2026; republico.app los lleva) */
/* ------------------------------------------------------------------ */

const LOGO_URL = "https://asrgzcggnwnsvpcsmxxw.supabase.co/storage/v1/object/public/media/brand/RECINE_black.svg";

export function organizationLd(): Json {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "RE:CINE",
    url: `${SITE_ORIGIN}/`,
    logo: LOGO_URL,
    areaServed: CITIES.map((c) => ({ "@type": "City", name: c.name })),
  };
}

export function webSiteLd(): Json {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "RE:CINE",
    url: `${SITE_ORIGIN}/`,
    inLanguage: "es",
  };
}
